import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { Trophy, TrendingUp, DollarSign, Target } from 'lucide-react';

const COLORS = ['#f6c90e','#60a5fa','#4ade80','#fb923c','#c084fc','#f87171','#2dd4bf'];

const TOOLTIP_STYLE = {
  contentStyle: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:8 },
  labelStyle:   { color:'#e2e8f0', fontWeight:600 },
  itemStyle:    { color:'#e2e8f0' },
};

const S = {
  heading: { fontSize:22, fontWeight:700, color:'#f6c90e', marginBottom:4 },
  sub: { fontSize:13, color:'#475569', marginBottom:24 },
  podium: { display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(200px,1fr))', gap:16, marginBottom:28 },
  card: (rank) => ({
    background:'#0e1420', border:`1px solid ${rank===0?'#f6c90e33':rank===1?'#60a5fa33':'#4ade8033'}`,
    borderRadius:14, padding:20, position:'relative', overflow:'hidden',
  }),
  rankBadge: (rank) => ({
    position:'absolute', top:12, right:12,
    width:28, height:28, borderRadius:'50%',
    background: rank===0?'#f6c90e':rank===1?'#60a5fa':'#4ade80',
    display:'flex', alignItems:'center', justifyContent:'center',
    fontSize:13, fontWeight:800, color:'#080c14',
  }),
  name: { fontSize:16, fontWeight:700, color:'#e2e8f0', marginBottom:4 },
  value: (c) => ({ fontSize:24, fontWeight:800, color:c, marginBottom:2 }),
  label: { fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1 },
  stat: { display:'flex', justifyContent:'space-between', padding:'6px 0', borderTop:'1px solid #1a2035', marginTop:8 },
  statLabel: { fontSize:12, color:'#475569' },
  statVal: { fontSize:12, color:'#cbd5e1', fontWeight:600 },
  chartCard: { background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, padding:20, marginBottom:16 },
  chartTitle: { fontSize:14, fontWeight:600, color:'#cbd5e1', marginBottom:16 },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { padding:'10px 14px', textAlign:'left', fontSize:11, color:'#475569', textTransform:'uppercase', letterSpacing:1, borderBottom:'1px solid #1a2035', whiteSpace:'nowrap' },
  td: { padding:'12px 14px', fontSize:13, color:'#cbd5e1', borderBottom:'1px solid #0f1117', whiteSpace:'nowrap' },
  sectionTitle: { fontSize:16, fontWeight:700, color:'#f6c90e', marginBottom:14 },
  sectionTitleRow: { display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14, flexWrap:'wrap', gap:12 },
  toggleWrap: { display:'flex', background:'#0e1420', border:'1px solid #1a2035', borderRadius:9, padding:3, gap:3 },
  toggleBtn: (active) => ({
    padding:'7px 14px', borderRadius:7, fontSize:12, fontWeight:600, cursor:'pointer', border:'none',
    background: active ? '#f6c90e' : 'transparent', color: active ? '#080c14' : '#94a3b8',
  }),
};

S.thSticky = { ...S.th, position:'sticky', top:0, background:'#0e1420', zIndex:2 };

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.innerWidth <= breakpoint
  );
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpoint]);
  return isMobile;
}

const fmtDate = (iso) => iso ? new Date(iso).toLocaleDateString('pt-BR') : '—';
const fmtDias = (n) => (n === null || n === undefined) ? '—' : `${n} dia${n === 1 ? '' : 's'}`;

const INFO_LEAD = {
  atribuicao:  'De onde vem: data em que o cliente foi atribuído ao vendedor atual (na criação do lead ou em uma reatribuição).',
  retorno:     'De onde vem: prazo definido para o vendedor retornar o contato com o cliente.',
  tempoRetorno:'Conta feita: Tempo de Retorno = Data Retorno − Data Atribuição',
  visita:      'De onde vem: data da visita agendada com o cliente.',
  aguardando:  'De onde vem: data em que o status do lead mudou para "Aguardando Cliente".',
  desistencia: 'De onde vem: data em que o status do lead mudou para "Desistiu da Compra".',
  proposta:    'De onde vem: última data em que a proposta foi gerada para este cliente.',
  venda:       'De onde vem: data em que o status do lead mudou para "Fechado" (venda concluída).',
  diasCompra:  'Conta feita: Dias para Compra = Data da Venda − Data Atribuição',
  agilidade:   'Conta feita: Agilidade = Data Visita − Data Atribuição',
  respostaCliente: 'Conta feita: Tempo de Resposta do Cliente = Data da Venda − Data Proposta Gerada',
  primeiraAcao: 'De onde vem: a data mais antiga entre Visita, Proposta Gerada, Aguardando Cliente, Desistência ou Venda registrada para este cliente — ou seja, a primeira ação tomada pelo vendedor.',
};

const INFO_RANKING = {
  ticketMedio: 'Conta feita: Ticket Médio de Venda = Receita Total ÷ Vendas Fechadas (apenas vendedores com pelo menos 1 venda fechada).',
};

const INFO_VENDEDOR = {
  clientes:        'De onde vem: total de clientes/leads já atribuídos a este vendedor (todos os status).',
  tempoRetornoMed: 'Conta feita: média de (Data Retorno − Data Atribuição) entre os clientes do vendedor com as duas datas registradas.',
  diasCompraMed:   'Conta feita: média de (Data da Venda − Data Atribuição) entre os clientes do vendedor que já compraram.',
  agilidadeMed:    'Conta feita: média de (Data Visita − Data Atribuição) entre os clientes do vendedor com visita registrada.',
  respostaClienteMed: 'Conta feita: média de (Data da Venda − Data Proposta Gerada) entre os clientes do vendedor que compraram após receber proposta.',
  aguardando:      'De onde vem: quantidade de clientes do vendedor com status atual "Aguardando Cliente".',
  desistencias:    'De onde vem: quantidade de clientes do vendedor com status atual "Desistiu da Compra".',
  propostas:       'De onde vem: quantidade de clientes do vendedor para os quais já foi gerada uma proposta.',
  fechadas:        'De onde vem: quantidade de clientes do vendedor com status atual "Fechado".',
};

function ThInfo({ label, info, sticky }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('click', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('click', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [open]);

  return (
    <th style={sticky ? S.thSticky : S.th}>
      <span ref={ref} style={{ position:'relative', display:'inline-flex', alignItems:'center', gap:5 }}>
        <span
          style={{ cursor:'pointer', display:'inline-flex', alignItems:'center', gap:5 }}
          onClick={(e) => { e.stopPropagation(); setOpen(o => !o); }}
        >
          {label}
          <span style={{
            fontSize:9, color:'#64748b', border:'1px solid #334155', borderRadius:'50%',
            width:13, height:13, display:'inline-flex', alignItems:'center', justifyContent:'center',
            lineHeight:1, flexShrink:0,
          }}>?</span>
        </span>
        {open && (
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position:'absolute', top:'calc(100% + 8px)', left:0, marginTop:0,
              background:'#1a2035', border:'1px solid #2d3748', borderRadius:8,
              padding:'10px 12px', fontSize:11, fontWeight:400, color:'#cbd5e1',
              textTransform:'none', letterSpacing:0, width:230, whiteSpace:'normal',
              zIndex:30, boxShadow:'0 8px 24px rgba(0,0,0,0.45)', lineHeight:1.4,
            }}
          >
            {info}
          </div>
        )}
      </span>
    </th>
  );
}

function aggregarPorVendedor(leads) {
  const map = {};
  leads.forEach(l => {
    if (!map[l.vendedor_id]) {
      map[l.vendedor_id] = {
        vendedor_id: l.vendedor_id, vendedor_nome: l.vendedor_nome, qtd: 0,
        somaRetorno: 0, nRetorno: 0, somaCompra: 0, nCompra: 0,
        somaAgilidade: 0, nAgilidade: 0, somaRespostaCliente: 0, nRespostaCliente: 0,
        aguardando: 0, desistencias: 0, propostas: 0, fechadas: 0,
      };
    }
    const g = map[l.vendedor_id];
    g.qtd += 1;
    if (l.tempo_retorno_dias !== null && l.tempo_retorno_dias !== undefined) { g.somaRetorno += l.tempo_retorno_dias; g.nRetorno += 1; }
    if (l.dias_para_compra !== null && l.dias_para_compra !== undefined) { g.somaCompra += l.dias_para_compra; g.nCompra += 1; }
    if (l.agilidade_dias !== null && l.agilidade_dias !== undefined) { g.somaAgilidade += l.agilidade_dias; g.nAgilidade += 1; }
    if (l.tempo_resposta_cliente_dias !== null && l.tempo_resposta_cliente_dias !== undefined) { g.somaRespostaCliente += l.tempo_resposta_cliente_dias; g.nRespostaCliente += 1; }
    if (l.status === 'em_espera') g.aguardando += 1;
    if (l.status === 'perdido') g.desistencias += 1;
    if (l.proposta_gerada) g.propostas += 1;
    if (l.status === 'fechado') g.fechadas += 1;
  });
  return Object.values(map).sort((a, b) => a.vendedor_nome.localeCompare(b.vendedor_nome));
}

export default function TopVendedores({ filtroInicio = '', filtroFim = '' }) {
  const [top, setTop] = useState(null);
  const [kpiLeads, setKpiLeads] = useState(null);
  const [kpiView, setKpiView] = useState('vendedor');
  const [erro, setErro] = useState('');
  const navigate = useNavigate();
  const { isChefe, isGerenteComercial } = useAuth();
  const podeAbrirPerformance = isChefe() || isGerenteComercial();
  const isMobile = useIsMobile();
  useEffect(() => {
    let cancelled = false;
    const _p = new URLSearchParams();
    if (filtroInicio) _p.set('data_inicio', filtroInicio);
    if (filtroFim)   _p.set('data_fim', filtroFim);
    const params = _p.toString() ? `?${_p}` : '';
    setTop(null);
    setKpiLeads(null);
    setErro('');
    api.get(`/top-vendedores/${params}`)
      .then(r => { if (!cancelled) setTop(r.data); })
      .catch(() => { if (!cancelled) setErro('Erro ao carregar dados. Tente atualizar a página.'); });
    api.get(`/vendedor-kpis-leads/${params}`)
      .then(r => { if (!cancelled) setKpiLeads(r.data); })
      .catch(() => { if (!cancelled) setKpiLeads([]); });
    return () => { cancelled = true; };
  }, [filtroInicio, filtroFim]); // eslint-disable-line
  if (erro) return <div style={{ background:'rgba(248,113,113,0.1)', border:'1px solid rgba(248,113,113,0.3)', borderRadius:8, padding:'10px 14px', color:'#f87171', fontSize:13 }}>{erro}</div>;
  if (!top) return <div style={{ color:'#475569' }}>Carregando...</div>;
  const chartData = top.map(v => ({ nome: v.nome.split(' ')[0], receita: v.receita, leads: v.total_leads }));
  const goPerformance = (v) => { if (podeAbrirPerformance) navigate(`/vendedor/${v.id}/performance`); };
  const kpiPorVendedor = kpiLeads ? aggregarPorVendedor(kpiLeads) : [];

  // Linhas visíveis sem rolar: 10 no computador, 5 no celular.
  const ROW_H = 44;
  const HEAD_H = 40;
  const maxRowsLead = isMobile ? 5 : 10;
  const leadTableMaxHeight = HEAD_H + ROW_H * maxRowsLead;

  return (
    <div>
      <div style={S.heading}>🏆 Top Vendedores</div>
      <div style={S.sub}>Ranking de desempenho da equipe de vendas</div>

      {/* Podium */}
      <div style={S.podium}>
        {top.slice(0, 3).map((v, i) => (
          <div key={v.id} style={S.card(i)}>
            <div style={S.rankBadge(i)}>{i+1}</div>
            <div style={{ fontSize:20, marginBottom:8 }}>{i===0?'🥇':i===1?'🥈':'🥉'}</div>
            <div
              style={{ ...S.name, cursor: podeAbrirPerformance ? 'pointer' : 'default', textDecoration: podeAbrirPerformance ? 'underline' : 'none', textDecorationColor:'rgba(246,201,14,0.4)' }}
              onClick={() => goPerformance(v)}
            >{v.nome}</div>
            <div style={S.value(COLORS[i])}>R$ {(v.receita/1000).toFixed(1)}k</div>
            <div style={S.label}>Receita total</div>
            <div style={S.stat}><span style={S.statLabel}>Leads</span><span style={S.statVal}>{v.total_leads}</span></div>
            <div style={S.stat}><span style={S.statLabel}>Fechados</span><span style={S.statVal}>{v.fechados}</span></div>
            <div style={S.stat}><span style={S.statLabel}>Conversão</span><span style={S.statVal}>{v.taxa}%</span></div>
          </div>
        ))}
      </div>

      {/* Charts */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16, marginBottom:20 }}>
        <div style={S.chartCard}>
          <div style={S.chartTitle}>Receita por Vendedor (R$)</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} layout="vertical">
              <XAxis type="number" tick={{ fill:'#475569', fontSize:11 }} axisLine={false} tickLine={false} tickFormatter={v => `${(v/1000).toFixed(0)}k`} />
              <YAxis type="category" dataKey="nome" tick={{ fill:'#94a3b8', fontSize:12 }} axisLine={false} tickLine={false} width={70} />
              <Tooltip {...TOOLTIP_STYLE} formatter={v => [`R$ ${v.toLocaleString('pt-BR')}`, 'Receita']} />
              <Bar dataKey="receita" radius={[0,4,4,0]}>
                {chartData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div style={S.chartCard}>
          <div style={S.chartTitle}>Leads por Vendedor</div>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={chartData} layout="vertical">
              <XAxis type="number" tick={{ fill:'#475569', fontSize:11 }} axisLine={false} tickLine={false} />
              <YAxis type="category" dataKey="nome" tick={{ fill:'#94a3b8', fontSize:12 }} axisLine={false} tickLine={false} width={70} />
              <Tooltip {...TOOLTIP_STYLE} />
              <Bar dataKey="leads" radius={[0,4,4,0]}>
                {chartData.map((_, i) => <Cell key={i} fill={`${COLORS[i % COLORS.length]}99`} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Full table */}
      <div style={S.sectionTitle}>Receita e Leads de Vendedores</div>
      <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, overflowX:'auto', overflowY:'auto', maxHeight:400, marginBottom:28 }}>
        <table style={S.table}>
          <thead>
            <tr>
              <th style={S.th}>Rank</th>
              <th style={S.th}>Vendedor</th>
              <th style={S.th}>Total Leads</th>
              <th style={S.th}>Fechados</th>
              <th style={S.th}>Taxa Conversão</th>
              <th style={S.th}>Receita</th>
              <ThInfo label="Ticket Médio" info={INFO_RANKING.ticketMedio} />
            </tr>
          </thead>
          <tbody>
            {top.map((v, i) => (
              <tr key={v.id}
                onMouseEnter={e => e.currentTarget.style.background='#111827'}
                onMouseLeave={e => e.currentTarget.style.background=''}>
                <td style={S.td}>
                  <span style={{ fontSize:16 }}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':`#${i+1}`}</span>
                </td>
                <td style={S.td}>
                  <strong
                    style={{ color:'#e2e8f0', cursor: podeAbrirPerformance ? 'pointer' : 'default', textDecoration: podeAbrirPerformance ? 'underline' : 'none', textDecorationColor:'rgba(246,201,14,0.4)' }}
                    onClick={() => goPerformance(v)}
                  >{v.nome}</strong>
                </td>
                <td style={S.td}>{v.total_leads}</td>
                <td style={S.td}>{v.fechados}</td>
                <td style={S.td}>
                  <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                    <div style={{ flex:1, height:6, background:'#1a2035', borderRadius:3, maxWidth:80 }}>
                      <div style={{ width:`${v.taxa}%`, height:'100%', background: COLORS[i % COLORS.length], borderRadius:3 }} />
                    </div>
                    <span>{v.taxa}%</span>
                  </div>
                </td>
                <td style={S.td}>
                  <span style={{ color:'#f6c90e', fontWeight:700 }}>
                    R$ {v.receita.toLocaleString('pt-BR')}
                  </span>
                </td>
                <td style={S.td}>
                  {v.fechados > 0 ? `R$ ${(v.receita / v.fechados).toLocaleString('pt-BR', { maximumFractionDigits: 0 })}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* KPIs dos Vendedores */}
      <div style={S.sectionTitleRow}>
        <div style={{ ...S.sectionTitle, marginBottom:0 }}>KPIs dos Vendedores</div>
        <div style={S.toggleWrap}>
          <button style={S.toggleBtn(kpiView === 'vendedor')} onClick={() => setKpiView('vendedor')}>Por vendedor</button>
          <button style={S.toggleBtn(kpiView === 'lead')} onClick={() => setKpiView('lead')}>Por cliente/lead</button>
        </div>
      </div>

      {!kpiLeads ? (
        <div style={{ color:'#475569' }}>Carregando...</div>
      ) : kpiView === 'vendedor' ? (
        <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, overflowX:'auto', overflowY:'auto', maxHeight:400 }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Vendedor</th>
                <ThInfo label="Clientes" info={INFO_VENDEDOR.clientes} />
                <ThInfo label="Tempo de Retorno Médio" info={INFO_VENDEDOR.tempoRetornoMed} />
                <ThInfo label="Dias para Compra Médio" info={INFO_VENDEDOR.diasCompraMed} />
                <ThInfo label="Agilidade Média" info={INFO_VENDEDOR.agilidadeMed} />
                <ThInfo label="Tempo de Resposta do Cliente Médio" info={INFO_VENDEDOR.respostaClienteMed} />
                <ThInfo label="Aguardando Cliente" info={INFO_VENDEDOR.aguardando} />
                <ThInfo label="Desistências" info={INFO_VENDEDOR.desistencias} />
                <ThInfo label="Propostas Geradas" info={INFO_VENDEDOR.propostas} />
                <ThInfo label="Vendas Fechadas" info={INFO_VENDEDOR.fechadas} />
              </tr>
            </thead>
            <tbody>
              {kpiPorVendedor.length === 0 ? (
                <tr><td style={S.td} colSpan={10}>Nenhum dado encontrado.</td></tr>
              ) : kpiPorVendedor.map(g => {
                return (
                  <tr key={g.vendedor_id}
                    onMouseEnter={e => e.currentTarget.style.background='#111827'}
                    onMouseLeave={e => e.currentTarget.style.background=''}>
                    <td style={S.td}><strong style={{ color:'#e2e8f0' }}>{g.vendedor_nome}</strong></td>
                    <td style={S.td}>{g.qtd}</td>
                    <td style={S.td}>{g.nRetorno > 0 ? fmtDias(Math.round(g.somaRetorno / g.nRetorno)) : '—'}</td>
                    <td style={S.td}>{g.nCompra > 0 ? fmtDias(Math.round(g.somaCompra / g.nCompra)) : '—'}</td>
                    <td style={S.td}>{g.nAgilidade > 0 ? fmtDias(Math.round(g.somaAgilidade / g.nAgilidade)) : '—'}</td>
                    <td style={S.td}>{g.nRespostaCliente > 0 ? fmtDias(Math.round(g.somaRespostaCliente / g.nRespostaCliente)) : '—'}</td>
                    <td style={S.td}>{g.aguardando}</td>
                    <td style={S.td}>{g.desistencias}</td>
                    <td style={S.td}>{g.propostas}</td>
                    <td style={S.td}>{g.fechadas}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <div style={{ background:'#0e1420', border:'1px solid #1a2035', borderRadius:12, overflow:'auto', maxHeight: leadTableMaxHeight }}>
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.thSticky}>Vendedor</th>
                <th style={S.thSticky}>Cliente</th>
                <th style={S.thSticky}>Status</th>
                <ThInfo sticky label="Data Atribuição" info={INFO_LEAD.atribuicao} />
                <ThInfo sticky label="Data Retorno" info={INFO_LEAD.retorno} />
                <ThInfo sticky label="Tempo de Retorno" info={INFO_LEAD.tempoRetorno} />
                <ThInfo sticky label="Data Visita" info={INFO_LEAD.visita} />
                <ThInfo sticky label="Aguardando Cliente" info={INFO_LEAD.aguardando} />
                <ThInfo sticky label="Desistência" info={INFO_LEAD.desistencia} />
                <ThInfo sticky label="Proposta Gerada" info={INFO_LEAD.proposta} />
                <ThInfo sticky label="Data da Venda" info={INFO_LEAD.venda} />
                <ThInfo sticky label="Dias para Compra" info={INFO_LEAD.diasCompra} />
                <ThInfo sticky label="Agilidade" info={INFO_LEAD.agilidade} />
                <ThInfo sticky label="Tempo de Resposta do Cliente" info={INFO_LEAD.respostaCliente} />
                <ThInfo sticky label="Primeira Ação Registrada" info={INFO_LEAD.primeiraAcao} />
              </tr>
            </thead>
            <tbody>
              {kpiLeads.length === 0 ? (
                <tr><td style={S.td} colSpan={15}>Nenhum dado encontrado.</td></tr>
              ) : kpiLeads.map(l => (
                <tr key={l.lead_id}
                  onMouseEnter={e => e.currentTarget.style.background='#111827'}
                  onMouseLeave={e => e.currentTarget.style.background=''}>
                  <td style={S.td}><strong style={{ color:'#e2e8f0' }}>{l.vendedor_nome}</strong></td>
                  <td style={S.td}>{l.cliente_nome}</td>
                  <td style={S.td}>{l.status_label}</td>
                  <td style={S.td}>{fmtDate(l.data_atribuicao)}</td>
                  <td style={S.td}>{fmtDate(l.data_retorno)}</td>
                  <td style={S.td}>{fmtDias(l.tempo_retorno_dias)}</td>
                  <td style={S.td}>{fmtDate(l.data_visita)}</td>
                  <td style={S.td}>{fmtDate(l.data_aguardando_cliente)}</td>
                  <td style={S.td}>{fmtDate(l.data_desistencia)}</td>
                  <td style={S.td}>{fmtDate(l.data_proposta_gerada)}</td>
                  <td style={S.td}>{fmtDate(l.data_venda)}</td>
                  <td style={S.td}>{fmtDias(l.dias_para_compra)}</td>
                  <td style={S.td}>{fmtDias(l.agilidade_dias)}</td>
                  <td style={S.td}>{fmtDias(l.tempo_resposta_cliente_dias)}</td>
                  <td style={S.td}>{l.primeira_acao_label ? `${l.primeira_acao_label} — ${fmtDate(l.primeira_acao_data)}` : '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
